import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Autocomplete,
  Marker,
  DirectionsService,
  DirectionsRenderer,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import SearchIcon from "@mui/icons-material/Search";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { TextField } from "@mui/material";

const containerStyle = {
  width: "100%",
  height: "500px",
};

const defaultCenter = {
  lat: 28.6139,
  lng: 77.209,
};

const LocationSearchOPDNetwork = ({ selectedLatLong, clinicData = [] }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyBEPIxaUWpcJwnUbeBkuEV5ct0u11YhyMo",
    libraries: ["places"],
  });
  console.log("clinicData", clinicData);

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: false,
      zoomControl: true,
    }),
    []
  );

  const autoCompleteRef = useRef(null);
  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(defaultCenter);
  const [showMarker, setShowMarker] = useState({
    latitude: "",
    longitude: "",
    address: "",
    pincode: "",
    state: "",
    sub_locality: "",
    stateId: 0,
    cityId: "",
  });
  const [searchAddress, setSearchAddress] = useState("");
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [directionsResponse, setDirectionsResponse] = useState(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [userLocation, setUserLocation] = useState(null);

  const handleMapClick = (event) => {
    if (!geocoderRef.current) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    geocoderRef.current.geocode(
      { location: { lat, lng } },
      (results, status) => {
        if (status === "OK" && results[0]) {
          const place = results[0];

          // Extract address components
          const postalCodeComponent = place.address_components?.find(
            (component) => component.types.includes("postal_code")
          );
          const administrativeAreaComponent = place.address_components?.find(
            (component) =>
              component.types.includes("administrative_area_level_1")
          );
          const localityComponent = place.address_components?.find(
            (component) => component.types.includes("locality")
          );
          const subLocalityComponent = place.address_components?.find(
            (component) => component.types.includes("sublocality_level_1")
          );
          console.log(
            "onclick map address",
            postalCodeComponent?.long_name,
            administrativeAreaComponent,
            localityComponent,
            subLocalityComponent
          );
          setMarkerPosition({ lat, lng });
          setMapCenter({ lat, lng });
          setSelectedClinic(null);
          // Update location details
          setShowMarker({
            latitude: lat,
            longitude: lng,
            address: place.formatted_address,
            pincode: postalCodeComponent?.long_name || "",
            state: administrativeAreaComponent?.long_name || "",
            sub_locality:
              subLocalityComponent?.long_name ||
              localityComponent?.long_name ||
              "",
            cityId: "",
            stateId: 0,
          });

          setSearchAddress(place.formatted_address);
        } else {
          console.error("Reverse geocoding failed:", status);
        }
      }
    );
  };

  const handleOnPlacesChanged = () => {
    try {
      let place = autoCompleteRef.current.getPlaces();
      console.log("address", place);
      if (place[0] && place[0].geometry) {
        const location = place[0].geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        const address = place[0].formatted_address;
        const postalCodeComponent = place[0].address_components?.find(
          (component) => component.types.includes("postal_code")
        );
        const administrativeAreaComponent = place[0].address_components?.find(
          (component) => component.types.includes("administrative_area_level_1")
        );
        const localityComponent = place[0].address_components?.find(
          (component) => component.types.includes("locality")
        );
        const pincode = postalCodeComponent?.long_name;
        const state = administrativeAreaComponent?.long_name;
        const subLocality = localityComponent?.long_name;
        const place_id = place[0].place_id;
        setMapCenter({ lat, lng });
        setMarkerPosition({ lat, lng });
        setSearchAddress(address);
        setSelectedClinic(null);
        setShowMarker({
          latitude: lat,
          longitude: lng,
          address: address,
          pincode: pincode,
          state: state,
          sub_locality: subLocality,
          cityId: "",
        });

        // Clear old directions
        setDirectionsResponse(null);
        setDistance("");
        setDuration("");
        console.log(
          "lat long",
          lat,
          lng,
          address,
          pincode,
          subLocality,
          place_id
        );
      }
    } catch (error) {
      console.error("Error processing place:", error);
    }
  };

  // console.log(showMarker);
  // console.log("New Marker Position:", markerPosition);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const current = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        console.log("current location", current);

        setUserLocation(current);
        setMapCenter(current);
        setMarkerPosition(current);
      });
    }
  }, []);

  // Handle selectedLatLong prop changes (when clinic is clicked)
  useEffect(() => {
    if (
      selectedLatLong &&
      selectedLatLong.latitude &&
      selectedLatLong.longitude
    ) {
      const clinicLocation = {
        lat: parseFloat(selectedLatLong.latitude),
        lng: parseFloat(selectedLatLong.longitude),
      };

      setMarkerPosition(clinicLocation);
      setMapCenter(clinicLocation);

      // Find the clinic in clinicData
      const clinic = clinicData.clinics.find(
        (c) =>
          c.latitude === selectedLatLong.latitude &&
          c.longitude === selectedLatLong.longitude
      );
      setSelectedClinic(clinic);

      // Clear old directions first
      setDirectionsResponse(null);
      setDistance("");
      setDuration("");

      // Automatically get directions when clinic is selected
      if (userLocation) {
        setDirectionsResponse("pending");
      }

      // Reverse geocode to get address for the selected clinic
      if (!geocoderRef.current && window.google) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }

      if (geocoderRef.current) {
        geocoderRef.current.geocode(
          { location: clinicLocation },
          (results, status) => {
            if (status === "OK" && results[0]) {
              setSearchAddress(results[0].formatted_address);
            }
          }
        );
      }
    }
  }, [selectedLatLong, userLocation]);

  // handle Directions
  const handleGetDirections = () => {
    if (!userLocation) {
      alert("User location not found");
      return;
    }
    if (!markerPosition) {
      alert("Please select a destination");
      return;
    }
    // Will trigger DirectionsService
    setDirectionsResponse("pending");
  };

  const handleDirectionsCallback = (res) => {
    if (res !== null && res.status === "OK") {
      setDirectionsResponse(res);
      const route = res.routes[0].legs[0];
      setDistance(route.distance.text);
      setDuration(route.duration.text);
    } else {
      console.error("Directions request failed:", res?.status);
      setDirectionsResponse(null);
      alert("Could not calculate directions");
    }
  };

  // Handle clinic marker click
  const handleClinicMarkerClick = (clinic) => {
    const clinicLocation = {
      lat: parseFloat(clinic.latitude),
      lng: parseFloat(clinic.longitude),
    };
    setSelectedClinic(clinic);
    setMarkerPosition(clinicLocation);
    setMapCenter(clinicLocation);

    // Update showMarker with clinic details
    setShowMarker({
      latitude: clinic.latitude,
      longitude: clinic.longitude,
      address: clinic.address,
      pincode: clinic.pincode || "",
      state: clinic.state || "",
      sub_locality: clinic.city || "",
      cityId: "",
      stateId: 0,
    });

    setSearchAddress(clinic.address || clinic.clinicName);

    // Clear old directions first
    setDirectionsResponse(null);
    setDistance("");
    setDuration("");
    // Get directions to this clinic
    if (userLocation) {
      setDirectionsResponse("pending");
    }
  };

  // Error handling for map loading
  if (loadError) {
    return (
      <div>
        Error loading Google Maps: {loadError.message}
        <br />
        Please check your API key and internet connection.
      </div>
    );
  }

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }

  return (
    <div>
      {isLoaded && (
        <StandaloneSearchBox
          onLoad={(ref) => (autoCompleteRef.current = ref)}
          onPlacesChanged={handleOnPlacesChanged}
        >
          <TextField
            type="text"
            placeholder="Search location or Enter Pincode"
            variant="outlined"
            id="autocomplete"
            size="small"
            value={searchAddress}
            onChange={(e) => setSearchAddress(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "20px",
                backgroundColor: "#ffffff",
              },
            }}
            fullWidth
            InputProps={{
              startAdornment: <MyLocationIcon />,
              endAdornment: <SearchIcon />,
            }}
          />
        </StandaloneSearchBox>
      )}

      <button
        onClick={handleGetDirections}
        style={{
          marginBottom: "10px",
          padding: "8px 12px",
          background: "#2d256c",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
        }}
      >
        Get Directions from My Location
      </button>

      {selectedClinic && (
        <div
          style={{
            marginBottom: "10px",
            padding: "10px",
            background: "#e3f2fd",
            borderRadius: "6px",
            borderLeft: "4px solid #2d256c",
          }}
        >
          <strong>{selectedClinic.clinicName}</strong>
          <p style={{ margin: "5px 0 0 0", fontSize: "13px" }}>
            {selectedClinic.address}
          </p>
        </div>
      )}

      {distance && duration && (
        <div
          style={{
            marginBottom: "10px",
            padding: "10px",
            background: "#f1f1f1",
            borderRadius: "4px",
          }}
        >
          <p>
            Distance: <strong>{distance}</strong> | Duration:{" "}
            <strong>{duration}</strong>
          </p>
        </div>
      )}
      {isLoaded && (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={14}
          options={mapOptions}
          onLoad={(map) => (mapRef.current = map)}
          onClick={handleMapClick}
        >
          {/* User location marker */}
          {userLocation && (
            <Marker
              position={userLocation}
              title="Your Location"
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                scaledSize: new window.google.maps.Size(30, 30),
              }}
            />
          )}

          {/* Selected clinic marker */}
          {showMarker?.latitude && showMarker?.longitude && (
            <Marker
              position={markerPosition}
              draggable={true}
              onDragEnd={handleMapClick}
              title={showMarker.address || "Selected Clinic"}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                scaledSize: new window.google.maps.Size(30, 30),
              }}
            />
          )}

          {/* All clinic markers */}
          {clinicData?.clinics?.map((clinic, index) => {
            const isSelected =
              showMarker?.latitude === clinic.latitude &&
              showMarker?.longitude === clinic.longitude;

            return (
              <Marker
                key={index}
                position={{
                  lat: parseFloat(clinic.latitude),
                  lng: parseFloat(clinic.longitude),
                }}
                title={clinic.clinicName}
                onClick={() => handleClinicMarkerClick(clinic)}
                icon={{
                  url: isSelected
                    ? "https://maps.google.com/mapfiles/ms/icons/red-dot.png" // red cross
                    : "https://cdn-icons-png.flaticon.com/512/2966/2966489.png",
                  scaledSize: new window.google.maps.Size(35, 35),
                }}
              />
            );
          })}

          {/* Directions service and renderer */}
          {directionsResponse === "pending" && (
            <DirectionsService
              options={{
                destination: markerPosition,
                origin: userLocation,
                travelMode: "DRIVING",
              }}
              callback={handleDirectionsCallback}
            />
          )}
          {directionsResponse !== null && directionsResponse !== "pending" && (
            <DirectionsRenderer directions={directionsResponse} />
          )}
        </GoogleMap>
      )}
    </div>
  );
};

export default LocationSearchOPDNetwork;
